// src/components/Modules/Vehicles/ViewVehicleDialog.js

import React, { useMemo } from "react";
import {
  Modal,
  Tag,
  Tabs,
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Tooltip,
  Progress,
} from "antd";
import {
  CarOutlined,
  UserOutlined,
  FileProtectOutlined,
  SettingOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  IdcardOutlined,
  CalendarOutlined,
  DashboardOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import "./ViewVehicleDialog.css";
import { textoSiNoSinDefinir } from "../../common/SiNoSinDefinir";

const { Text, Title } = Typography;

const ViewVehicleDialog = ({ vehicle, open, handleClose }) => {
  // Documentos con vencimiento - debe estar antes de cualquier return condicional
  const documentosVencimiento = useMemo(() => {
    if (!vehicle) return [];
    return [
      { key: "preventiva", label: "Preventiva", fecha: vehicle.fecha_vencimiento_preventiva },
      { key: "soat", label: "SOAT", fecha: vehicle.fecha_vencimiento_soat },
      { key: "tecnomecanica", label: "Tecnomecánica", fecha: vehicle.fecha_vencimiento_tecnomecanica },
      { key: "contractual", label: "Póliza Contractual", fecha: vehicle.fecha_vencimiento_contractual },
      { key: "extracontractual", label: "Póliza Extracontractual", fecha: vehicle.fecha_vencimiento_extracontractual },
      { key: "tarjeta_operacion", label: "Tarjeta de Operación", fecha: vehicle.fecha_vencimiento_tarjeta_operacion },
      { key: "todo_riesgo", label: "Todo Riesgo", fecha: vehicle.fecha_vencimiento_todo_riesgo },
      // Opcional: solo cuenta si se registró
      ...(vehicle.fecha_vencimiento_convenio
        ? [{ key: "convenio", label: "Convenio", fecha: vehicle.fecha_vencimiento_convenio }]
        : []),
    ];
  }, [vehicle]);

  // Early return después de los hooks
  if (!vehicle) return null;

  // Función para formatear fecha
  const formatDate = (date) => {
    if (!date) return "No registrada";
    return dayjs(date).format("DD/MM/YYYY");
  };

  // Función para verificar si una fecha está vencida
  const isExpired = (date) => {
    if (!date) return false;
    return dayjs(date).isBefore(dayjs(), "day");
  };

  // Función para verificar si está próximo a vencer (30 días)
  const isNearExpiry = (date) => {
    if (!date) return false;
    const daysUntilExpiry = dayjs(date).diff(dayjs(), "day");
    return daysUntilExpiry >= 0 && daysUntilExpiry <= 30;
  };

  // Calcular días restantes
  const getDaysRemaining = (date) => {
    if (!date) return null;
    return dayjs(date).diff(dayjs(), "day");
  };

  // Obtener color del estado
  const getEstadoColor = (estado) => {
    const colorMap = {
      activo: "green",
      inactivo: "orange",
      suspendido: "red",
      retirado: "default",
    };
    return colorMap[estado?.toLowerCase()] || "default";
  };

  // Contar documentos vencidos
  const documentosVencidos = documentosVencimiento.filter(doc => isExpired(doc.fecha)).length;
  const documentosProximos = documentosVencimiento.filter(doc => isNearExpiry(doc.fecha)).length;

  // Renderizar item de información
  const InfoItem = ({ icon, label, value, fullWidth = false }) => (
    <Col xs={24} sm={fullWidth ? 24 : 12} md={fullWidth ? 24 : 8}>
      <div className="view-vehicle-info-item">
        <div className="view-vehicle-info-icon">{icon}</div>
        <div className="view-vehicle-info-content">
          <Text type="secondary" className="view-vehicle-info-label">{label}</Text>
          <Text strong className="view-vehicle-info-value">{value || "No registrado"}</Text>
        </div>
      </div>
    </Col>
  );

  // Renderizar documento con estado
  const DocumentItem = ({ label, fecha }) => {
    const expired = isExpired(fecha);
    const nearExpiry = isNearExpiry(fecha);
    const daysRemaining = getDaysRemaining(fecha);

    let statusIcon, statusColor, statusText;
    if (!fecha) {
      statusIcon = <ClockCircleOutlined />;
      statusColor = "#8c8c8c";
      statusText = "Sin fecha";
    } else if (expired) {
      statusIcon = <WarningOutlined />;
      statusColor = "#ff4d4f";
      statusText = `Vencido hace ${Math.abs(daysRemaining)} días`;
    } else if (nearExpiry) {
      statusIcon = <WarningOutlined />;
      statusColor = "#faad14";
      statusText = `Vence en ${daysRemaining} días`;
    } else {
      statusIcon = <CheckCircleOutlined />;
      statusColor = "#52c41a";
      statusText = `${daysRemaining} días restantes`;
    }

    return (
      <div className="view-vehicle-doc-item">
        <div className="view-vehicle-doc-header">
          <Text strong>{label}</Text>
          <Tooltip title={statusText}>
            <span style={{ color: statusColor }}>{statusIcon}</span>
          </Tooltip>
        </div>
        <Text type="secondary">{formatDate(fecha)}</Text>
        {fecha && (
          <Progress
            percent={expired ? 100 : Math.max(0, 100 - (daysRemaining / 365) * 100)}
            size="small"
            status={expired ? "exception" : nearExpiry ? "active" : "success"}
            showInfo={false}
            strokeColor={statusColor}
          />
        )}
      </div>
    );
  };

  // Tab: Información del Vehículo
  const VehicleInfoTab = () => (
    <div className="view-vehicle-tab-content">
      <Card className="view-vehicle-card">
        <div className="view-vehicle-header-card">
          <div className="view-vehicle-avatar">
            <CarOutlined />
          </div>
          <div className="view-vehicle-header-info">
            <Title level={4} style={{ margin: 0 }}>{vehicle.placa}</Title>
            <Text type="secondary">{vehicle.marca} {vehicle.modelo} - {vehicle.linea || "N/A"}</Text>
            <div style={{ marginTop: 8 }}>
              <Tag color={getEstadoColor(vehicle.estado)} style={{ fontSize: 14, padding: "4px 12px" }}>
                {vehicle.estado}
              </Tag>
              {vehicle.genera_fuec === false && <Tag color="orange">No genera FUEC</Tag>}
              {vehicle.genera_preoperacional === false && <Tag color="orange">No genera preoperacional</Tag>}
              {documentosVencidos > 0 && (
                <Tag color="error" icon={<WarningOutlined />}>
                  {documentosVencidos} doc. vencidos
                </Tag>
              )}
              {documentosProximos > 0 && (
                <Tag color="warning" icon={<ClockCircleOutlined />}>
                  {documentosProximos} por vencer
                </Tag>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Divider orientation="left">Datos Básicos</Divider>
      <Row gutter={[16, 16]}>
        <InfoItem icon={<IdcardOutlined />} label="Código" value={vehicle.codigo} />
        <InfoItem icon={<CarOutlined />} label="Placa" value={vehicle.placa} />
        <InfoItem icon={<FileProtectOutlined />} label="N° Tarjeta Operación" value={vehicle.numero_tarjeta_operacion} />
        <InfoItem icon={<CarOutlined />} label="Marca" value={vehicle.marca} />
        <InfoItem icon={<CarOutlined />} label="Modelo" value={vehicle.modelo} />
        <InfoItem icon={<CarOutlined />} label="Línea" value={vehicle.linea} />
        <InfoItem icon={<CarOutlined />} label="Tipo" value={vehicle.tipo} />
        <InfoItem icon={<CarOutlined />} label="Color" value={vehicle.color} />
        <InfoItem icon={<EnvironmentOutlined />} label="Zona" value={vehicle.zona?.nombre} />
      </Row>

      <Divider orientation="left">Datos Técnicos</Divider>
      <Row gutter={[16, 16]}>
        <InfoItem icon={<SettingOutlined />} label="N° Motor" value={vehicle.numero_motor} />
        <InfoItem icon={<SettingOutlined />} label="N° Chasis" value={vehicle.numero_chasis} />
        <InfoItem icon={<DashboardOutlined />} label="Kilometraje" value={vehicle.kilometraje ? `${vehicle.kilometraje.toLocaleString()} km` : null} />
        <InfoItem icon={<SettingOutlined />} label="Cilindraje" value={vehicle.cilindraje ? `${vehicle.cilindraje} cc` : null} />
        <InfoItem icon={<UserOutlined />} label="Capacidad Pasajeros" value={vehicle.capacidad_pasajeros} />
        <InfoItem icon={<SettingOutlined />} label="Combustible" value={vehicle.combustible} />
        <InfoItem icon={<EnvironmentOutlined />} label="GPS" value={vehicle.gps} />
      </Row>
    </div>
  );

  // Tab: Propietario
  const OwnerInfoTab = () => (
    <div className="view-vehicle-tab-content">
      <Card className="view-vehicle-card">
        <div className="view-vehicle-section-header">
          <UserOutlined className="view-vehicle-section-icon" />
          <Title level={5} style={{ margin: 0 }}>Datos del Propietario</Title>
        </div>
      </Card>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <InfoItem icon={<UserOutlined />} label="Nombre" value={vehicle.propietario_nombre} />
        <InfoItem icon={<IdcardOutlined />} label="Documento" value={vehicle.propietario_numero_documento} />
        <InfoItem icon={<PhoneOutlined />} label="Teléfono" value={vehicle.propietario_telefono} />
        <InfoItem icon={<MailOutlined />} label="Correo" value={vehicle.propietario_correo} />
        <InfoItem icon={<EnvironmentOutlined />} label="Dirección" value={vehicle.propietario_direccion} fullWidth />
        <InfoItem icon={<EnvironmentOutlined />} label="Barrio" value={vehicle.propietario_barrio} />
        <InfoItem icon={<EnvironmentOutlined />} label="Localidad" value={vehicle.propietario_localidad} />
        <InfoItem icon={<EnvironmentOutlined />} label="Ciudad" value={vehicle.propietario_ciudad_rel?.nombre || vehicle.propietario_ciudad} />
        <InfoItem icon={<EnvironmentOutlined />} label="Departamento" value={vehicle.propietario_departamento_rel?.nombre || vehicle.propietario_departamento} />
      </Row>
    </div>
  );

  // Tab: Administrador
  const AdminInfoTab = () => (
    <div className="view-vehicle-tab-content">
      <Card className="view-vehicle-card">
        <div className="view-vehicle-section-header">
          <UserOutlined className="view-vehicle-section-icon" />
          <Title level={5} style={{ margin: 0 }}>Datos del Administrador</Title>
          {vehicle.administrador_acceso_app && (
            <Tag color="green" style={{ marginLeft: 8 }}>Ingreso a plataforma</Tag>
          )}
        </div>
      </Card>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <InfoItem icon={<UserOutlined />} label="Nombre" value={vehicle.administrador_nombre} />
        <InfoItem icon={<IdcardOutlined />} label="Documento" value={vehicle.administrador_numero_documento} />
        <InfoItem icon={<PhoneOutlined />} label="Teléfono" value={vehicle.administrador_telefono} />
        <InfoItem icon={<MailOutlined />} label="Correo" value={vehicle.administrador_correo} />
        <InfoItem icon={<EnvironmentOutlined />} label="Dirección" value={vehicle.administrador_direccion} fullWidth />
        <InfoItem icon={<EnvironmentOutlined />} label="Barrio" value={vehicle.administrador_barrio} />
        <InfoItem icon={<EnvironmentOutlined />} label="Localidad" value={vehicle.administrador_localidad} />
        <InfoItem icon={<EnvironmentOutlined />} label="Ciudad" value={vehicle.administrador_ciudad_rel?.nombre || vehicle.administrador_ciudad} />
        <InfoItem icon={<EnvironmentOutlined />} label="Departamento" value={vehicle.administrador_departamento_rel?.nombre || vehicle.administrador_departamento} />
      </Row>
    </div>
  );

  // Tab: Convenio
  const ContractInfoTab = () => (
    <div className="view-vehicle-tab-content">
      <Card className="view-vehicle-card">
        <div className="view-vehicle-section-header">
          <FileProtectOutlined className="view-vehicle-section-icon" />
          <Title level={5} style={{ margin: 0 }}>Información del Convenio</Title>
        </div>
      </Card>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <InfoItem icon={<FileProtectOutlined />} label="Tipo Vinculación" value={vehicle.tipo_vinculacion} />
        <InfoItem icon={<CalendarOutlined />} label="Fecha Vinculación" value={formatDate(vehicle.fecha_vinculacion)} />
        <InfoItem icon={<CalendarOutlined />} label="Fecha Matrícula" value={formatDate(vehicle.fecha_matricula)} />
        <InfoItem icon={<FileProtectOutlined />} label="Empresa Vinculadora" value={vehicle.empresa_vinculadora} />
        <InfoItem icon={<IdcardOutlined />} label="NIT" value={vehicle.nit} />
        <InfoItem icon={<CarOutlined />} label="Flota propia" value={textoSiNoSinDefinir(vehicle.flota_propia)} />
        <Col xs={24} sm={12} md={8}>
          <Card size="small" className="view-vehicle-doc-card">
            <DocumentItem label="Vencimiento convenio" fecha={vehicle.fecha_vencimiento_convenio} />
          </Card>
        </Col>
      </Row>
    </div>
  );

  // Tab: Documentos
  const DocumentsInfoTab = () => (
    <div className="view-vehicle-tab-content">
      <Card className="view-vehicle-card">
        <div className="view-vehicle-section-header">
          <SafetyCertificateOutlined className="view-vehicle-section-icon" />
          <Title level={5} style={{ margin: 0 }}>Fechas de Vencimiento</Title>
        </div>
        {(documentosVencidos > 0 || documentosProximos > 0) && (
          <div style={{ marginTop: 12 }}>
            {documentosVencidos > 0 && (
              <Tag color="error" icon={<WarningOutlined />} style={{ marginBottom: 4 }}>
                {documentosVencidos} documento(s) vencido(s)
              </Tag>
            )}
            {documentosProximos > 0 && (
              <Tag color="warning" icon={<ClockCircleOutlined />}>
                {documentosProximos} documento(s) próximo(s) a vencer
              </Tag>
            )}
          </div>
        )}
      </Card>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {documentosVencimiento.map((doc) => (
          <Col xs={24} sm={12} md={8} key={doc.key}>
            <Card size="small" className="view-vehicle-doc-card">
              <DocumentItem label={doc.label} fecha={doc.fecha} />
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );

  const tabItems = [
    {
      key: "vehiculo",
      label: (
        <span>
          <CarOutlined /> Vehículo
        </span>
      ),
      children: <VehicleInfoTab />,
    },
    {
      key: "propietario",
      label: (
        <span>
          <UserOutlined /> Propietario
        </span>
      ),
      children: <OwnerInfoTab />,
    },
    {
      key: "administrador",
      label: (
        <span>
          <UserOutlined /> Administrador
        </span>
      ),
      children: <AdminInfoTab />,
    },
    {
      key: "convenio",
      label: (
        <span>
          <FileProtectOutlined /> Convenio
        </span>
      ),
      children: <ContractInfoTab />,
    },
    {
      key: "documentos",
      label: (
        <span>
          <SafetyCertificateOutlined /> Documentos
          {documentosVencidos > 0 && (
            <Tag color="error" style={{ marginLeft: 8, fontSize: 10 }}>{documentosVencidos}</Tag>
          )}
        </span>
      ),
      children: <DocumentsInfoTab />,
    },
  ];

  return (
    <Modal
      title={
        <div className="view-vehicle-modal-header">
          <CarOutlined className="view-vehicle-modal-icon" />
          <span>Detalles del Vehículo</span>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={900}
      className="view-vehicle-modal"
      centered
    >
      <Tabs
        defaultActiveKey="vehiculo"
        items={tabItems}
        className="view-vehicle-tabs"
      />
    </Modal>
  );
};

ViewVehicleDialog.propTypes = {
  vehicle: PropTypes.object,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default ViewVehicleDialog;
